// Libraries
import React from 'react';

// Supermove
import {DataLoader, Form, Space, Styled} from '@supermove/components';
import {gql, useQuery} from '@supermove/graphql';
import {useState} from '@supermove/hooks';
import {OrganizationModel, UserModel} from '@supermove/models';
import {colors} from '@supermove/styles';

// App
import ResponsiveBadge from '@shared/design/components/Badge/ResponsiveBadge';
import EditPanel from '@shared/design/components/Panel/EditPanel';
import Text from '@shared/design/components/Text';
import CalendarUsersForm from '@shared/modules/Inventory/forms/CalendarUsersForm';
import useUpdateCalendarUsersMutation from '@shared/modules/Inventory/hooks/useUpdateCalendarUsersMutation';
import OrganizationSalesPersonWithCalendarSelect from 'modules/Organization/components/OrganizationSalesPersonWithCalendarSelect';

const LoadingIndicator = Styled.Loading`
`;

const Column = Styled.View`
  flex: 1;
`;

const Row = Styled.View`
  flex-direction: row;
  flex-wrap: wrap;
`;

const BodyComponent = ({organization}: {organization: OrganizationModel}) => {
  return (
    <Column style={{flex: 1}}>
      <Column style={{flex: 1}}>
        <Text.Label>Sales Calendar</Text.Label>
      </Column>
      <Space height={4} />
      {organization.allCalendarUsers.length > 0 ? (
        <Row>
          {organization.allCalendarUsers.map((calendarUser) => (
            <React.Fragment key={calendarUser.id}>
              <ResponsiveBadge label={calendarUser.user.fullName} style={{marginBottom: 4}} />
              <Space width={4} />
            </React.Fragment>
          ))}
        </Row>
      ) : (
        <Column style={{flex: 1}}>
          <Text.Body style={{color: colors.gray.tertiary}}>None</Text.Body>
        </Column>
      )}
    </Column>
  );
};

const EditBodyComponent = ({
  data,
  loading,
  form,
}: {
  data: {viewer: UserModel} | undefined;
  loading: boolean;
  form: Form;
}) => {
  return (
    <DataLoader data={data} loading={loading} as={LoadingIndicator}>
      {({loadedData}) => {
        return (
          <OrganizationSalesPersonWithCalendarSelect
            organization={loadedData.viewer.viewingOrganization}
            form={form}
          />
        );
      }}
    </DataLoader>
  );
};

const SurveyCalendarUsersPanel = ({
  index,
  organization,
  refetch,
}: {
  index: number;
  organization: OrganizationModel;
  refetch: () => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const calendarUsersForm = CalendarUsersForm.edit(organization);

  // Separated query to avoid loading calendars when not editing
  const {data, loading} = useQuery<{viewer: UserModel}>(SurveyCalendarUsersPanel.query, {
    skip: !isEditing,
  });

  const {form, handleSubmit, submitting} = useUpdateCalendarUsersMutation({
    calendarUsersForm,
    onSuccess: refetch,
    onError: (errors) => {
      console.log({errors});
    },
  });

  return (
    <EditPanel
      index={index}
      title={'Sales Calendar'}
      headerSubtitle={
        'Select calendars to show when scheduling survey jobs to check availabilities.'
      }
      isDisabled={submitting}
      handleSave={handleSubmit}
      handleEdit={() => setIsEditing(true)}
      handleClose={() => setIsEditing(false)}
      isEditing={isEditing}
      BodyComponent={() => <BodyComponent organization={organization} />}
      EditBodyComponent={() => <EditBodyComponent data={data} loading={loading} form={form} />}
    />
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
SurveyCalendarUsersPanel.query = gql`
  ${OrganizationSalesPersonWithCalendarSelect.fragment}

  query SurveyCalendarUsersPanel {
    ${gql.query}
    viewer {
      id
      viewingOrganization {
        id
        ...OrganizationSalesPersonWithCalendarSelect
      }
    }
  }`;

SurveyCalendarUsersPanel.fragment = gql`
  ${CalendarUsersForm.edit.fragment}
  fragment SurveyCalendarUsersPanel_Organization on Organization {
    id
    allCalendarUsers {
      id
      user {
        id
        fullName
      }
    }
    ...CalendarUsersForm_edit
  }
`;

export default SurveyCalendarUsersPanel;
